/**
 * Pending deep link store (R8.2).
 *
 * COLD START RACE CONDITION:
 * When the app is completely closed and the user taps a push notification,
 * the `pushNotificationActionPerformed` event fires BEFORE React Router and
 * AuthContext have finished initializing. A direct `navigate()` call at that
 * point will either fail silently or be immediately overwritten by the
 * ProtectedRoute redirect to /login.
 *
 * Solution: store the intended target route in a module-level variable
 * (survives React re-renders and context re-initializations).
 * The DeepLinkNavigator component polls this variable once auth is stable
 * and navigates to the stored path.
 *
 * Module-level (not React state) so it persists across provider re-mounts.
 */

let _pendingDeepLink: string | null = null;

/** Store a target route to navigate to once auth resolves. */
export function setPendingDeepLink(path: string): void {
  _pendingDeepLink = path;
}

/**
 * Consume and clear the pending deep link.
 * Returns null if no link is pending.
 */
export function consumePendingDeepLink(): string | null {
  const link = _pendingDeepLink;
  _pendingDeepLink = null;
  return link;
}

/** Build a navigation path from the push notification data payload. */
export function buildDeepLinkFromData(
  resourceType?: string,
  resourceId?: string,
): string | null {
  if (!resourceType || !resourceId) return null;

  switch (resourceType) {
    case 'WORK_REPORT':
      return `/app/reports/${resourceId}`;
    case 'DOCUMENT':
      return `/app/documents/${resourceId}`;
    case 'MILESTONE':
      // No milestone detail page yet — navigate to dashboard
      return '/app/dashboard';
    default:
      return null;
  }
}
